import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { useClearGuests } from './queries';

/**
 * Emptying the guest list.
 *
 * **Typing the wedding's name, not an "Are you sure?".** A confirm dialog is
 * dismissed by the same reflex that opened it, and this is the only action in the
 * app that cannot be undone — there is no bin and no export taken on the way out.
 * Copying a name is a small deliberate act, and it is checked again on the server,
 * so a browser cannot skip it.
 *
 * The count is in the question. "Delete all guests?" is abstract; "this removes
 * 143 people" is the fact that makes somebody stop.
 */
export function ClearGuestsDialog({
  wedId,
  weddingTitle,
  guestCount,
  open,
  onClose,
}: {
  wedId: string;
  weddingTitle: string;
  guestCount: number;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const clear = useClearGuests(wedId);
  const [typed, setTyped] = useState('');

  const matches = typed.trim() === weddingTitle.trim();

  return (
    <Drawer open={open} title={t('guests.clearTitle')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-body">{t('guests.clearWhat', { count: guestCount })}</p>
        <p className="text-body text-status-declinedFg">{t('guests.clearNoUndo')}</p>
        {/* Said plainly rather than discovered afterwards: the seating plan is a
            drawing of a room and survives, but every chair it had people in is
            now empty. */}
        <p className="text-caption text-leaf-soft">{t('guests.clearKeepsTables')}</p>

        <label className="flex flex-col gap-1">
          <span className="text-caption text-leaf-soft">
            {t('guests.clearConfirmLabel', { title: weddingTitle })}
          </span>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="min-h-tap rounded border border-line bg-paper px-2"
          />
        </label>

        {clear.isError && (
          <p role="alert" className="text-body text-status-declinedFg">
            {t('guests.clearFailed')}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary"
            disabled={!matches || clear.isPending}
            loading={clear.isPending}
            onClick={() =>
              clear.mutate(
                { confirm: typed.trim() },
                {
                  onSuccess: () => {
                    setTyped('');
                    onClose();
                  },
                },
              )
            }
          >
            {t('guests.clearAction')}
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
